import { Icon } from "@iconify/react";
import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useStore } from "../../hooks/useStore";
import CurrencyFormatter from "../../utils/CurrencyFormatter";
import { API_URL } from "../../config/api";

const ProductGrid = () => {
  const { 
    products, 
    loading, 
    error, 
    loadProducts,
    goToPrevPage,
    goToNextPage,
    goToPage,
    pagination
  } = useStore();

  useEffect(() => {
    // Carregar produtos reais quando o componente monta
    loadProducts(1);
  }, [loadProducts]);

  // Função para limpar URLs de imagem duplicadas
  const cleanImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    
    // Se a URL está duplicada, extrair apenas a parte correta
    const duplicatePattern = `${API_URL}/api/images/${API_URL}/api/images/`;
    if (imageUrl.includes(duplicatePattern)) {
      return imageUrl.replace(duplicatePattern, `${API_URL}/api/images/`);
    }
    
    return imageUrl;
  };

  // Função para obter o nome da categoria
  const getCategoryName = (categoryId) => {
    const categories = {
      1: "Administração e Negócios",
      2: "Animais de Estimação", 
      3: "Arquitetura e Engenharia",
      4: "Artes e Música",
      5: "Auto-ajuda e Desenvolvimento Pessoal",
      6: "Automóveis",
      7: "Blogs e Redes Sociais",
      8: "Casa e Jardinagem",
      9: "Culinária, Gastronomia, Receitas",
      10: "Design e Templates PSD, PPT ou HTML",
      11: "Edição de Áudio, Vídeo ou Imagens",
      12: "Educacional, Cursos Técnicos e Profissionalizantes",
      13: "Entretenimento, Lazer e Diversão",
      14: "Esportes e Fitness",
      15: "Filmes e Cinema",
      16: "Geral",
      17: "Histórias em Quadrinhos",
      18: "Idiomas",
      19: "Informática",
      20: "Internet Marketing"
    };
    
    return categories[categoryId] || `Categoria ${categoryId}`;
  };

  // Debug: log dos produtos para verificar as imagens
  useEffect(() => {
    if (products && products.length > 0) {
      console.log('🛍️ Produtos da loja carregados:');
      products.forEach(p => {
        const cleanImage = cleanImageUrl(p.image);
        console.log(`📦 ${p.name}:`, {
          id: p.id,
          category: p.category,
          categoryName: getCategoryName(p.category),
          originalImage: p.image,
          cleanedImage: cleanImage,
          hasImage: !!cleanImage
        });
      });
    }
  }, [products]);

  const ProgressWithCircle = ({ score }) => {
    // Atualizado: máximo é 200 PAGs (conforme documentação)
    const percent = Math.min((score / 200) * 100, 100);

    return (
      <div className="position-relative w-100 mt-3" style={{ height: "30px" }}>
        {/* Barra de progresso */}
        <div
          className="progress h-8-px w-100 bg-neutral-200 radius-8 overflow-hidden"
          style={{
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          <div
            className="rounded-pill"
            style={{
              width: `${percent}%`,
              height: "8px",
              background: "#44ADD4",
            }}
          />
        </div>

        {/* Círculo com valor e label */}
        <div
          className="position-absolute"
          style={{
            left: `${percent}%`,
            top: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{
              width: "38px",
              height: "38px",
              borderRadius: "50%",
              background: "#44ADD4",
              color: "white",
              fontSize: "11px",
              fontWeight: "600",
              boxShadow: "0 0 4px rgba(0,0,0,0.1)",
              padding: "2px",
            }}
          >
            <div style={{ fontSize: "11px", lineHeight: "12px" }}>{score}</div>
            <div style={{ fontSize: "8px", opacity: 0.8, lineHeight: "10px" }}>
              Pag's
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24 text-center">
          <div className="d-flex align-items-center justify-content-center gap-3">
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
            <span className="text-secondary-light">Carregando produtos...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24 text-center">
          <Icon
            icon="solar:danger-triangle-linear"
            className="text-6xl text-danger mb-3"
          />
          <h6 className="text-lg mb-2">Erro ao carregar produtos</h6>
          <p className="text-secondary-light mb-3">
            {error}
          </p>
          <button 
            onClick={() => loadProducts(true)}
            className="btn btn-primary-600 text-white px-24 py-12 radius-8"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  // Garantir que products seja sempre um array
  if (!Array.isArray(products)) {
    return (
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24 text-center">
          <div className="d-flex align-items-center justify-content-center gap-3">
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
            <span className="text-secondary-light">Carregando produtos...</span>
          </div>
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24 text-center">
          <Icon
            icon="solar:box-linear"
            className="text-6xl text-secondary-light mb-3"
          />
          <h6 className="text-lg mb-2">Nenhum produto encontrado</h6>
          <p className="text-secondary-light mb-0">
            Tente ajustar os filtros ou busca para encontrar produtos.
          </p>
        </div>
      </div>
    );
  }

  // Renderizar produtos
  const renderProducts = () => {
    // Verificar se products é um array válido
    if (!Array.isArray(products)) {
      return (
        <div className="col-12 text-center py-4">
          <div className="text-secondary-light">
            <Icon icon="solar:box-linear" className="me-2" />
            Carregando produtos...
          </div>
        </div>
      );
    }

    return (
      <>
        {products.map((product, index) => (
          <div 
            key={product.id || index} 
            className="col-xxl-3 col-lg-4 col-md-6 user-grid-card"
          >
            <Link 
              to={`/store/${product.uuid}`}
              className="text-decoration-none"
              style={{ display: 'block', height: '100%' }}
            >
              <div className="position-relative border radius-16 overflow-hidden h-100 bg-white shadow-sm hover-shadow-lg transition-all" style={{ cursor: 'pointer' }}>
                
                {/* Badge de Categoria no Topo */}
                <div className="position-absolute top-12 start-12 z-3">
                  <span className="badge bg-primary-600 text-white px-10 py-6 text-xs fw-medium radius-6 shadow-sm">
                    {getCategoryName(product.category)}
                  </span>
                </div>

              {/* Imagem do Produto */}
              <div 
                className="position-relative bg-light d-flex align-items-center justify-content-center overflow-hidden"
                style={{ height: "200px" }}
              >
                {/* Badge After Pay - Base inferior direita da imagem com margem correta */}
                {product.has_after_pay === true && (
                  <div className="position-absolute" style={{ bottom: '16px', right: '16px', zIndex: 3 }}>
                    <span className="badge bg-success-600 text-white px-8 py-4 text-xs fw-medium radius-6 shadow-sm">
                      <Icon icon="solar:dollar-circle-bold" className="me-1" />
                      After Pay
                    </span>
                  </div>
                )}
                <img
                  src={cleanImageUrl(product.image) || "https://cdn.pixabay.com/photo/2021/08/03/06/47/clock-6518632_1280.jpg"}
                  alt={product.name}
                  className="w-100 h-100 object-fit-cover"
                  style={{ filter: 'brightness(0.9)' }}
                  onError={(e) => {
                    console.log('❌ Erro ao carregar imagem:', cleanImageUrl(product.image), 'para produto:', product.name);
                    e.target.src = "https://cdn.pixabay.com/photo/2021/08/03/06/47/clock-6518632_1280.jpg";
                  }}
                />
                
                {/* Gradiente sutil no bottom para melhor legibilidade */}
                <div 
                  className="position-absolute bottom-0 start-0 w-100"
                  style={{ 
                    height: "40px",
                    background: "linear-gradient(transparent, rgba(0,0,0,0.1))"
                  }}
                />
              </div>

              {/* Conteúdo do Card */}
              <div className="p-20">
                {/* Nome do Produto */}
                <div className="mb-12">
                  <h6 className="text-md mb-6 fw-semibold line-height-1-3" title={product.name}>
                    {product.name.length > 45 ? `${product.name.substring(0, 45)}...` : product.name}
                  </h6>
                  
                  {/* Código do Produto */}
                  <div className="d-flex align-items-center gap-2 mb-8">
                    <Icon icon="solar:hashtag-linear" className="text-xs text-secondary-light" />
                    <span className="text-xs text-secondary-light fw-medium font-monospace">
                      {product.product_code || product.code || 'N/A'}
                    </span>
                  </div>
                </div>

                {/* ProgressBar com Score */}
                <div className="mb-16">
                  <ProgressWithCircle score={product.score} />
                </div>

                {/* Produtor */}
                <div className="d-flex align-items-center gap-2 mb-12">
                  <Icon icon="solar:user-circle-linear" className="text-sm text-secondary-light" />
                  <span className="text-sm text-secondary-light">
                    {product.producer}
                  </span>
                </div>

                {/* Comissão */}
                <div className="mb-16">
                  <div className="text-center bg-neutral-50 radius-8 py-12">
                    <div className="text-xs text-secondary-light mt-1">
                      Comissão: {product.commission_percentage}%
                    </div>
                  </div>
                </div>

                {/* Preço do Produto */}
                <div className="mb-16">
                  <div className="text-center bg-primary-50 radius-8 py-12">
                    {product.min_price === product.max_price ? (
                      // Produto com apenas um plano
                      <>
                        <div className="text-primary-600 text-sm fw-medium mb-1">
                          Preço do Produto
                        </div>
                        <div className="text-primary-600 text-lg fw-bold">
                          {CurrencyFormatter.centsToFormatted(product.min_price)}
                        </div>
                      </>
                    ) : (
                      // Produto com múltiplos planos
                      <>
                        <div className="text-primary-600 text-sm fw-medium mb-1">
                          Faixa de Preços
                        </div>
                        <div className="text-primary-600 text-lg fw-bold">
                          De {CurrencyFormatter.centsToFormatted(product.min_price)} até {CurrencyFormatter.centsToFormatted(product.max_price)}
                        </div>
                      </>
                    )}
                  </div>
                </div>

              </div>
            </div>
            </Link>
          </div>
        ))}
        
        {/* Paginação com Números */}
        {pagination.totalPages > 1 && (
          <div className="col-12">
            <div className="d-flex flex-column align-items-center gap-3 mt-4">
              {/* Informações dos produtos */}
              <div className="text-center">
                <span className="text-secondary-light">
                  Mostrando {Math.min((pagination.page - 1) * pagination.limit + 1, pagination.total)} a {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total} produtos
                </span>
              </div>

              {/* Controles de Paginação */}
              <nav aria-label="Navegação da loja">
                <ul className="pagination justify-content-center mb-0">
                  {/* Botão Anterior */}
                  <li className={`page-item ${!pagination.hasPrevPage ? 'disabled' : ''}`}>
                    <button 
                      className="page-link d-flex align-items-center justify-content-center" 
                      style={{ minWidth: '40px', height: '40px' }}
                      onClick={goToPrevPage}
                      disabled={!pagination.hasPrevPage || loading}
                    >
                      <Icon icon="solar:arrow-left-linear" />
                    </button>
                  </li>

                  {/* Números das páginas */}
                  {(() => {
                    const pages = [];
                    const current = pagination.page;
                    const total = pagination.totalPages;
                    
                    // Lógica para mostrar páginas (máximo 7 números)
                    let startPage = Math.max(1, current - 3);
                    let endPage = Math.min(total, current + 3);
                    
                    // Ajustar se estiver no início
                    if (current <= 4) {
                      endPage = Math.min(7, total);
                    }
                    
                    // Ajustar se estiver no final
                    if (current > total - 4) {
                      startPage = Math.max(1, total - 6);
                    }

                    // Primeira página
                    if (startPage > 1) {
                      pages.push(
                        <li key={1} className="page-item">
                          <button 
                            className="page-link d-flex align-items-center justify-content-center" 
                            style={{ minWidth: '40px', height: '40px' }}
                            onClick={() => goToPage(1)}
                            disabled={loading}
                          >
                            1
                          </button>
                        </li>
                      );
                      
                      if (startPage > 2) {
                        pages.push(
                          <li key="start-ellipsis" className="page-item disabled">
                            <span className="page-link d-flex align-items-center justify-content-center" style={{ minWidth: '40px', height: '40px' }}>...</span>
                          </li>
                        );
                      }
                    }

                    // Páginas do meio
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <li key={i} className={`page-item ${i === current ? 'active' : ''}`}>
                          <button 
                            className="page-link d-flex align-items-center justify-content-center" 
                            style={{ minWidth: '40px', height: '40px' }}
                            onClick={() => goToPage(i)}
                            disabled={loading}
                          >
                            {i}
                          </button>
                        </li>
                      );
                    }

                    // Última página
                    if (endPage < total) {
                      if (endPage < total - 1) {
                        pages.push(
                          <li key="end-ellipsis" className="page-item disabled">
                            <span className="page-link d-flex align-items-center justify-content-center" style={{ minWidth: '40px', height: '40px' }}>...</span>
                          </li>
                        );
                      }
                      
                      pages.push(
                        <li key={total} className="page-item">
                          <button 
                            className="page-link d-flex align-items-center justify-content-center" 
                            style={{ minWidth: '40px', height: '40px' }}
                            onClick={() => goToPage(total)}
                            disabled={loading}
                          >
                            {total}
                          </button>
                        </li>
                      );
                    }

                    return pages;
                  })()}

                  {/* Botão Próximo */}
                  <li className={`page-item ${!pagination.hasNextPage ? 'disabled' : ''}`}>
                    <button 
                      className="page-link d-flex align-items-center justify-content-center" 
                      style={{ minWidth: '40px', height: '40px' }}
                      onClick={goToNextPage}
                      disabled={!pagination.hasNextPage || loading}
                    >
                      <Icon icon="solar:arrow-right-linear" />
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-body p-24">
        <div className="d-flex align-items-center justify-content-end mb-20">
          <div className="d-flex align-items-center gap-2">
            <Icon icon="solar:sort-vertical-linear" className="text-lg text-secondary-light" />
            <span className="text-sm text-secondary-light">Ordenar por relevância</span>
          </div>
        </div>
        

        
        <div className="row gy-4">
          {renderProducts()}
        </div>
      </div>
    </div>
  );
};

export default ProductGrid;
